<template>
  <div class="sub-root">
    <!-- ── TOP BAR ─────────────────────────────────── -->
    <header class="topbar">
      <div class="topbar-inner">
        <router-link to="/login" class="btn btn-outline-light btn-link">
          <i class="mdi mdi-arrow-left text-white"></i> Return to Login
        </router-link>
        <div class="topbar-brand">
          <router-link tag="a" to="">
            <img
              v-if="userData.logo != 0"
              :src="getFiles(userData.logo)"
              class="brand-logo"
            />
            <img v-else src="@/assets/images/logo.png" class="brand-logo" />
          </router-link>
          <div class="brand-text d-none d-md-block">
            <p class="brand-name">{{ userData.school }}</p>
            <div class="brand-meta">
              <span
                ><i class="fas fa-map-marker-alt"></i>
                {{ userData.location }}</span
              >
              <span
                ><i class="fas fa-phone-alt"></i> {{ userData.contact }}</span
              >
            </div>
          </div>
        </div>
        <b-dropdown
          right
          variant="link"
          toggle-class="user-toggle p-0"
          menu-class="user-menu shadow border-0"
        >
          <template v-slot:button-content>
            <div class="user-pill">
              <div class="user-avatar">
                <i class="mdi mdi-account-circle"></i>
              </div>
              <span class="user-name d-none d-md-inline">{{
                userData.username
              }}</span>
              <i class="mdi mdi-chevron-down"></i>
            </div>
          </template>
          <b-dropdown-item href="/logout" class="logout-item">
            <i class="bx bx-power-off me-2"></i>Logout
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </header>

    <!-- ── PAGE BODY ───────────────────────────────── -->
    <main class="page-body">
      <!-- Eyebrow + Heading -->
      <div class="page-heading">
        <span class="eyebrow">
          <i class="mdi mdi-star-four-points-outline"></i> Subscription Plans
        </span>
        <h1 class="page-title">Select a Service Package</h1>
        <p class="page-sub">
          Pick the perfect plan that suits your business needs
        </p>
      </div>

      <!-- ── FORM CARD ─────────────────────────────── -->
      <div class="form-card">
        <!-- Step 1 – Service -->
        <div class="field-group">
          <label class="field-label">
            <span class="label-num">01</span> Service Type
          </label>
          <div class="service-tabs">
            <button
              v-for="s in services"
              :key="s"
              type="button"
              class="service-tab"
              :class="{ active: service === s }"
              @click="
                service = s;
                FindProduct();
              "
            >
              <span class="tab-icon">
                <i
                  class="mdi"
                  :class="
                    s === 'Software Subscription'
                      ? 'mdi-laptop'
                      : 'mdi-message-text-outline'
                  "
                ></i>
              </span>
              <span class="tab-label">{{ s }}</span>
              <span class="tab-check"
                ><i class="mdi mdi-check-circle"></i
              ></span>
            </button>
          </div>
        </div>

        <!-- Step 2 – Package + Duration -->
        <div class="field-row">
          <div
            class="field-group"
            :class="
              service === 'Software Subscription' ? 'col-split' : 'col-full'
            "
          >
            <label class="field-label">
              <span class="label-num">02</span> Package
            </label>
            <v-select
              :options="products"
              @input="FindPrice"
              placeholder="Choose a package…"
              v-model="product"
              class="v-pkg"
            >
              <template #option="{ label }">
                <div class="pkg-option">
                  <i class="mdi mdi-package-variant-closed"></i>
                  <span>{{ label }}</span>
                </div>
              </template>
            </v-select>
          </div>

          <div
            class="field-group col-split"
            v-show="service === 'Software Subscription'"
          >
            <label class="field-label">
              <span class="label-num">03</span> Months
            </label>
            <div class="qty-control">
              <button
                type="button"
                class="qty-btn"
                @click="
                  monthsToPay > 1 && monthsToPay--;
                  CalculateAmount();
                "
                :disabled="monthsToPay <= 1"
              >
                <i class="mdi mdi-minus"></i>
              </button>
              <input
                v-model.number="monthsToPay"
                @change="CalculateAmount"
                type="number"
                min="1"
                class="qty-input"
              />
              <button
                type="button"
                class="qty-btn"
                @click="
                  monthsToPay++;
                  CalculateAmount();
                "
              >
                <i class="mdi mdi-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3 – Amount Display -->
        <div class="amount-strip">
          <div class="amount-label-group">
            <span class="amount-eyebrow">Total Payable</span>
            <div class="amount-value">
              <span class="currency">UGX</span>
              <span class="big-num">{{ formatAmount(calculatedAmount) }}</span>
            </div>
            <span
              class="per-month"
              v-if="service === 'Software Subscription' && monthsToPay > 1"
            >
              UGX {{ formatAmount(baseAmount) }} / month
            </span>
          </div>
        </div>

        <!-- Step 4 – Mobile Money -->
        <div class="field-group">
          <label class="field-label">
            <span class="label-num">{{
              service === "Software Subscription" ? "04" : "03"
            }}</span>
            Mobile Money Number
          </label>
          <div class="momo-input-wrap">
            <span class="momo-prefix">+256</span>
            <input
              v-model="telephone"
              v-mask="'#########'"
              type="tel"
              class="momo-input"
              placeholder="Enter MoMo number"
            />
            <span class="momo-icon"
              ><i class="mdi mdi-cellphone-wireless"></i
            ></span>
          </div>
          <p class="field-hint">
            <i class="mdi mdi-information-outline me-1"></i>
            A payment prompt will be sent to this number
          </p>
        </div>

        <!-- Action Row -->
        <div class="action-row">
          <button type="button" class="btn-ghost" @click="resetForm">
            <i class="mdi mdi-refresh me-1"></i> Reset
          </button>
          <button
            type="button"
            class="btn-pay"
            :disabled="!isFormValid"
            @click="pay"
          >
            <span class="btn-pay-inner">
              <i class="mdi mdi-lock-outline me-2"></i>
              Proceed to Payment
            </span>
          </button>
        </div>
      </div>

      <!-- Trust Row -->
      <div class="trust-row">
        <div class="trust-item">
          <div class="trust-icon">
            <i class="mdi mdi-shield-check-outline"></i>
          </div>
          <span>Secure Payment</span>
        </div>
        <div class="trust-sep"></div>
        <div class="trust-item">
          <div class="trust-icon"><i class="mdi mdi-clock-fast"></i></div>
          <span>Instant Processing</span>
        </div>
        <div class="trust-sep"></div>
        <div class="trust-item">
          <div class="trust-icon"><i class="mdi mdi-headset"></i></div>
          <span>24/7 Support</span>
        </div>
      </div>
    </main>

    <!-- ── WAITING MODAL ──────────────────────────── -->
    <b-modal id="waiting-confirm" centered hide-footer hide-header>
      <div class="modal-inner">
        <div class="modal-spinner-wrap">
          <div class="spinner-ring"></div>
          <div class="spinner-ring ring-2"></div>
          <i class="mdi mdi-cellphone-wireless modal-phone-icon"></i>
        </div>
        <h4 class="modal-title-txt">Processing Payment</h4>
        <p class="modal-msg">{{ responseMessage }}</p>
        <p class="modal-sub">Check your phone and approve the MoMo prompt</p>
        <button
          class="modal-close-btn"
          @click="$bvModal.hide('waiting-confirm')"
        >
          Dismiss
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import appConfig from "@/app.config";
import { subscriptionMethods, subscriptionComputed } from "@/state/helpers";

export default {
  page: {
    title: "Subscriptions",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "Subscriptions",
      items: [{ text: "Subscriptions", active: true }],
      TotalRows: 1,
      currentPage: 1,
      perPage: 10,
      Options: [10, 50, 100, 150, 200],
      filter: null,
      filterOn: [],
      sortDesc: true,
      isBusy: true,
      fields: [
        { key: "index", label: "#", visible: true },
        { key: "id", label: "ID", visible: false },
        { key: "service", label: "Service", visible: true },
        { key: "amount", label: "Amount", visible: true },
        { key: "telephone", label: "Telephone", visible: true },
        { key: "updated_at", label: "Paid On", visible: true },
      ],
      id: "",
      name: "",
      monthsToPay: 1,
      baseAmount: 0,
      services: ["Software Subscription", "SMS Purchase"],
      service: "",
      products: [],
      product: "",
      amount: "",
      telephone: "",
      responseMessage: "",
      paymentlink: "",
      options: {
        weekday: "short",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
      },
      interval: null,
    };
  },
  computed: {
    ...subscriptionComputed,
    List() {
      return this.subscriptions.length;
    },
    Fields() {
      return this.fields.filter((f) => f.visible);
    },
    Items() {
      return this.subscriptions.map((d) => ({ ...d }));
    },
    IsBusy() {
      return this.subscriptions.length === 0;
    },
    calculatedAmount() {
      return this.baseAmount * this.monthsToPay;
    },
    isFormValid() {
      return (
        this.service &&
        this.product &&
        this.telephone &&
        this.telephone.length === 9
      );
    },
  },
  mounted() {
    this.fetchSubscriptions();
    this.TotalRows = this.subscriptions.length;
    this.isBusy = false;
  },
  methods: {
    ...subscriptionMethods,
    formatAmount(amount) {
      return Number(amount)
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,");
    },
    resetForm() {
      this.service = "";
      this.product = "";
      this.telephone = "";
      this.monthsToPay = 1;
      this.baseAmount = 0;
    },
    Filtered(filteredItems) {
      this.TotalRows = filteredItems.length;
      this.currentPage = 1;
    },
    getFiles(name) {
      let url = axios.defaults.baseURL;
      return url.slice(0, -3) + "logos/" + name;
    },
    FindProduct() {
      if (this.service === "Software Subscription") {
        this.products = ["Termly Subscription"];
      } else {
        this.products = [
          "Starter Pack - 2500 sms",
          "Premium Pack - 5000 sms",
          "Enterprise Pack - 10000 sms",
        ];
      }
      this.product = "";
      this.baseAmount = 0;
      this.amount = 0;
      this.monthsToPay = 1;
    },
    FindPrice() {
      const prices = {
        "Termly Subscription": this.userData.monthly_charge,
        "Starter Pack - 2500 sms": 1000,
        "Premium Pack - 5000 sms": 30000,
        "Enterprise Pack - 10000 sms": 50000,
      };
      this.baseAmount = prices[this.product] || 0;
      this.CalculateAmount();
    },
    CalculateAmount() {
      if (!this.monthsToPay || this.monthsToPay < 1) this.monthsToPay = 1;
      this.amount = this.baseAmount * this.monthsToPay;
    },
    async pay() {
      const fd = new FormData();
      fd.append("telephone", this.telephone);
      fd.append("amount", this.amount);
      fd.append("service", this.product);
      fd.append("months_paid", this.monthsToPay);
      const { data } = await axios.post("/renewsubscription", fd);
      if (data.code === 200) {
        this.responseMessage = data.message;
        this.paymentlink = data.payment_link;
        window.open(data.payment_link, "_blank");
        this.$bvModal.show("waiting-confirm");
        this.interval = setInterval(
          () => this.ConfirmPayment(data.reference),
          25000,
        );
      }
    },
    async ConfirmPayment(reference) {
      const fd = new FormData();
      fd.append("reference", reference);
      const { data, status } = await axios.post("/confirmsubscription", fd);
      if (status === 200 && data.code === 200) {
        this.$bvModal.hide("waiting-confirm");
        clearInterval(this.interval);
        this.fetchSubscriptions();
        this.$swal({
          title: "Success",
          text: data.message + " License has been renewed.",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
        });
        this.resetForm();
        this.$router.push("/login");
      } else if (status === 200 && data.message == "failed") {
        this.$bvModal.hide("waiting-confirm");
        clearInterval(this.interval);
        this.resetForm();
        this.$swal({
          title: "Payment Failed",
          text: data.message + " Please try again or contact support.",
          icon: "error",
          timer: 4000,
          showConfirmButton: false,
        });
      }
    },
  },
  destroyed() {
    clearInterval(this.interval);
  },
};
</script>

<style scoped>
/* ── ROOT: lock to viewport height, no overflow ── */
.sub-root {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  position: relative;
}

/* ── TOP BAR ── */
.topbar {
  flex-shrink: 0;
  position: relative;
  z-index: 100;
  background: #ef2c01;
  box-shadow: 0 2px 12px rgba(3, 128, 106, 0.2);
}
.topbar-inner {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0.45rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.topbar-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.brand-logo {
  height: 36px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.brand-name {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}
.brand-meta {
  display: flex;
  gap: 1rem;
}
.brand-meta span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.72rem;
}
.brand-meta i {
  color: rgba(255, 255, 255, 0.9);
  margin-right: 3px;
}

/* User pill */
.user-pill {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50px;
  padding: 0.15rem 0.4rem;
  cursor: pointer;
  transition: background 0.2s;
}
.user-pill:hover {
  background: rgba(255, 255, 255, 0.28);
}
.user-avatar {
  font-size: 1.1rem;
  color: #fff;
  line-height: 1;
}
.user-name {
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
}
.user-pill .mdi-chevron-down {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

::v-deep .user-menu {
  border-radius: 12px !important;
  overflow: hidden;
  min-width: 130px;
}
::v-deep .logout-item a {
  color: #e05454 !important;
  font-size: 0.88rem;
  padding: 0.3rem 1rem;
}

/* ── PAGE BODY: fills remaining height, no scroll ── */
.page-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.6rem 1.25rem;
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
}

/* Heading */
.page-heading {
  text-align: center;
  margin-bottom: 0.55rem;
  flex-shrink: 0;
}
.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(
    135deg,
    rgba(197, 56, 56, 0.317),
    rgba(197, 56, 56, 0.317)
  );
  color: var(--g1);
  border: 1px solid rgba(128, 86, 3, 0.2);
  padding: 0.18rem 0.8rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
}
.page-title {
  font-size: clamp(1.2rem, 3vw, 1.6rem);
  font-weight: 500;
  color: var(--text);
  letter-spacing: -0.03em;
  margin-bottom: 0.2rem;
  line-height: 1.15;
}
.page-sub {
  color: var(--muted);
  font-size: 0.82rem;
  margin: 0;
}

/* ── FORM CARD ── */
.form-card {
  width: 100%;
  max-width: 660px;
  background: var(--surface);
  border-radius: 14px;
  border: 1px solid rgba(128, 128, 128, 0.322);
  box-shadow: 0 6px 28px rgba(197, 56, 56, 0.12), 0 2px 6px rgba(0, 0, 0, 0.04);
  padding: 0.9rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
}

/* Field groups */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.field-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.col-full {
  flex: 1 1 100%;
}
.col-split {
  flex: 1 1 calc(50% - 0.5rem);
  min-width: 140px;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(31, 30, 30, 0.911);
  margin: 0;
}
.label-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: rgba(128, 128, 128, 0.317);
  color: #fff;
  border-radius: 50%;
  font-size: 0.6rem;
  font-weight: 800;
  flex-shrink: 0;
}

/* Service tabs */
.service-tabs {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.service-tab {
  flex: 1;
  min-width: 140px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0.85rem;
  border: 1.5px solid rgba(128, 128, 128, 0.317);
  border-radius: 11px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}
.service-tab::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(197, 56, 56, 0.317),
    rgba(197, 56, 56, 0.317)
  );
  opacity: 0;
  transition: opacity 0.2s;
}
.service-tab:hover::before {
  opacity: 1;
}
.service-tab:hover {
  border-color: rgba(128, 128, 128, 0.317);
}
.service-tab.active {
  border-color: rgba(197, 56, 56, 0.317);
  background: linear-gradient(
    135deg,
    rgba(3, 128, 106, 0.06),
    rgba(2, 163, 138, 0.06)
  );
}
.service-tab.active .tab-icon {
  background: rgba(197, 56, 56, 0.317);
  color: #fff;
}
.service-tab.active .tab-label {
  color: var(--g1);
  font-weight: 600;
}
.service-tab.active .tab-check {
  opacity: 1;
}

.tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: #f0f6f4;
  border-radius: 8px;
  font-size: 0.88rem;
  color: var(--muted);
  transition: all 0.2s;
  flex-shrink: 0;
}
.tab-label {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text);
  flex: 1;
  text-align: left;
}
.tab-check {
  opacity: 0;
  color: var(--g1);
  font-size: 1rem;
  transition: opacity 0.2s;
}

/* v-select overrides */
::v-deep .v-pkg .vs__dropdown-toggle {
  border: 1.5px solid rgba(128, 128, 128, 0.317);
  border-radius: 10px;
  padding: 0.35rem 0.75rem;
  background: #fafcfb;
  transition: border-color 0.2s;
  min-height: 38px;
}
::v-deep .v-pkg.vs--open .vs__dropdown-toggle,
::v-deep .v-pkg .vs__dropdown-toggle:focus-within {
  border-color: rgba(128, 128, 128, 0.317);
  box-shadow: 0 0 0 3px rgba(197, 56, 56, 0.317);
}
::v-deep .v-pkg .vs__dropdown-menu {
  border-radius: 10px;
  border: 1px solid rgba(128, 128, 128, 0.317);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  margin-top: 3px;
}
::v-deep .v-pkg .vs__dropdown-option--highlight {
  background: rgba(248, 247, 247, 0.317);
}
.pkg-option {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.84rem;
  color: #0f0f0f;
}
.pkg-option i {
  color: #0f0f0f;
}

/* Quantity control */
.qty-control {
  display: flex;
  align-items: center;
  border: 1.5px solid rgba(128, 128, 128, 0.317);
  border-radius: 10px;
  overflow: hidden;
  background: #fafcfb;
  min-height: 38px;
}
.qty-btn {
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  cursor: pointer;
  color: var(--muted);
  transition: all 0.15s;
}
.qty-btn:hover:not(:disabled) {
  background: rgba(197, 56, 56, 0.317);
  color: var(--g1);
}
.qty-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.qty-input {
  flex: 1;
  border: none;
  outline: none;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
  background: transparent;
  padding: 0;
}

/* Amount strip */
.amount-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(
    135deg,
    rgba(197, 56, 56, 0.317) 0%,
    rgba(197, 56, 56, 0.317) 100%
  );
  border: 1.5px solid rgba(197, 56, 56, 0.317);
  border-radius: 12px;
  padding: 0.65rem 1.1rem;
}
.amount-label-group {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.amount-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}
.amount-value {
  display: flex;
  align-items: baseline;
  gap: 0.15rem;
  line-height: 1;
}
.currency {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--g1);
  margin-top: 0.1rem;
}
.big-num {
  font-size: 2rem;
  font-weight: 800;
  color: var(--g1);
  letter-spacing: -0.03em;
}
.per-month {
  font-size: 0.72rem;
  color: var(--muted);
  margin-top: 0.15rem;
}

/* MoMo Input */
.momo-input-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid rgba(197, 56, 56, 0.317);
  border-radius: 10px;
  overflow: hidden;
  background: #fafcfb;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.momo-input-wrap:focus-within {
  border-color: var(--g1);
  box-shadow: 0 0 0 3px rgba(197, 56, 56, 0.317);
}
.momo-prefix {
  padding: 0 0.75rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--g1);
  background: rgba(197, 56, 56, 0.317);
  border-right: 1.5px solid rgba(197, 56, 56, 0.317);
  height: 38px;
  display: flex;
  align-items: center;
}
.momo-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 0.75rem;
  font-size: 0.88rem;
  color: var(--text);
  background: transparent;
  height: 38px;
}
.momo-icon {
  padding: 0 0.75rem;
  color: var(--muted);
  font-size: 1rem;
  display: flex;
  align-items: center;
}
.field-hint {
  font-size: 0.72rem;
  color: var(--muted);
  margin: 0;
}

/* Action Row */
.action-row {
  display: flex;
  gap: 0.6rem;
}

.btn-ghost {
  padding: 0.4rem 1.1rem;
  border: 1.5px solid rgba(197, 56, 56, 0.317);
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.btn-ghost:hover {
  border-color: rgba(30, 30, 30, 0.317);
  color: rgba(197, 56, 56, 0.317);
  background: rgba(197, 56, 56, 0.317);
}

.btn-pay {
  flex: 1;
  padding: 0.55rem 1.25rem;
  border: none;
  border-radius: 10px;
  background: rgb(238, 76, 43);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 3px 12px rgba(200, 55, 32, 0.3);
  position: relative;
  overflow: hidden;
}
.btn-pay::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(197, 56, 56, 0.317), transparent);
  opacity: 0;
  transition: opacity 0.2s;
}
.btn-pay:hover:not(:disabled)::after {
  opacity: 1;
}
.btn-pay:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(197, 56, 56, 0.317);
}
.btn-pay:active:not(:disabled) {
  transform: translateY(0);
}
.btn-pay:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.btn-pay-inner {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── TRUST ROW ── */
.trust-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.55rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.trust-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--muted);
}
.trust-icon {
  font-size: 0.95rem;
  color: var(--g1);
}
.trust-sep {
  width: 3px;
  height: 3px;
  background: rgba(128, 128, 128, 0.317);
  border-radius: 50%;
}

/* ── MODAL ── */
.modal-inner {
  text-align: center;
  padding: 1.5rem 1.25rem 1.25rem;
}
.modal-spinner-wrap {
  position: relative;
  width: 68px;
  height: 68px;
  margin: 0 auto 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: var(--g1);
  animation: spin 1s linear infinite;
}
.spinner-ring.ring-2 {
  inset: 8px;
  border-top-color: var(--g2);
  animation-duration: 0.7s;
  animation-direction: reverse;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.modal-phone-icon {
  font-size: 1.5rem;
  color: var(--g1);
  position: relative;
  z-index: 1;
}
.modal-title-txt {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 0.4rem;
}
.modal-msg {
  color: var(--text);
  font-size: 0.9rem;
  margin-bottom: 0.2rem;
}
.modal-sub {
  color: var(--muted);
  font-size: 0.78rem;
  margin-bottom: 1.2rem;
}
.modal-close-btn {
  padding: 0.5rem 1.5rem;
  border: 1.5px solid rgba(128, 128, 128, 0.317);
  border-radius: 50px;
  background: transparent;
  color: var(--muted);
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.modal-close-btn:hover {
  border-color: var(--g1);
  color: var(--g1);
}

/* ── RESPONSIVE ── */
@media (max-width: 600px) {
  .form-card {
    padding: 0.75rem 0.9rem;
    gap: 0.6rem;
  }
  .service-tabs {
    flex-direction: column;
  }
  .service-tab {
    min-width: unset;
  }
  .action-row {
    flex-direction: column;
  }
  .btn-ghost {
    order: 2;
  }
  .btn-pay {
    order: 1;
  }
  .big-num {
    font-size: 1.6rem;
  }
  .col-split {
    flex: 1 1 100%;
  }
  .trust-sep {
    display: none;
  }
  .trust-row {
    gap: 0.5rem;
    justify-content: space-evenly;
  }
  .page-body {
    padding: 0.4rem 0.75rem;
  }
}
</style>
